class ProductIngredientsTabs extends HTMLElement {
  #controller = null;
  #tabs = [];
  #panels = [];
  #mediaQuery = null;
  #isDesktop = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#tabs = Array.from(this.querySelectorAll('[data-product-ingredients-tab]'));
    this.#panels = Array.from(this.querySelectorAll('[data-product-ingredients-panel]'));
    this.#mediaQuery = window.matchMedia('(min-width: 990px)');

    const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabs[0];

    if (activeTab) {
      this.#select(activeTab);
    }

    this.#syncInteractionMode();
    this.#mediaQuery.addEventListener('change', () => this.#syncInteractionMode(), { signal });

    for (const tab of this.#tabs) {
      tab.addEventListener(
        'click',
        () => {
          if (!this.#isDesktop) return;
          this.#select(tab);
        },
        { signal }
      );
      tab.addEventListener(
        'keydown',
        (event) => {
          if (!this.#isDesktop) return;
          this.#onKeyDown(event, tab);
        },
        { signal }
      );
      tab.addEventListener(
        'pointerenter',
        () => {
          if (!this.#isDesktop) return;
          this.#preloadPanelForTab(tab);
        },
        { signal }
      );
      tab.addEventListener(
        'focusin',
        () => {
          if (!this.#isDesktop) return;
          this.#preloadPanelForTab(tab);
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#tabs = [];
    this.#panels = [];
    this.#mediaQuery = null;
  }

  #syncInteractionMode() {
    this.#isDesktop = this.#mediaQuery?.matches ?? false;

    if (!this.#isDesktop) {
      for (const tab of this.#tabs) {
        tab.setAttribute('tabindex', '-1');
        tab.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    for (const tab of this.#tabs) {
      tab.removeAttribute('aria-disabled');
    }

    const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabs[0];
    if (activeTab) {
      this.#select(activeTab);
    }

    if (this.#isDesktop) {
      this.#schedulePreloadAllPanelImages();
    }
  }

  #select(tab) {
    const controls = tab.getAttribute('aria-controls');
    if (!controls) return;

    const targetPanel = this.#panels.find((panel) => panel.id === controls);
    this.#preloadPanelImage(targetPanel);

    for (const t of this.#tabs) {
      const isSelected = t === tab;
      t.setAttribute('aria-selected', String(isSelected));
      t.setAttribute('tabindex', this.#isDesktop && isSelected ? '0' : '-1');
    }

    for (const panel of this.#panels) {
      panel.hidden = panel.id !== controls;
    }
  }

  #preloadPanelForTab(tab) {
    const controls = tab.getAttribute('aria-controls');
    if (!controls) return;

    const panel = this.#panels.find((item) => item.id === controls);
    this.#preloadPanelImage(panel);
  }

  #preloadPanelImage(panel) {
    const image = panel?.querySelector('.product-ingredients-detail__image');
    if (!image || image.complete) return;

    image.loading = 'eager';

    const preloadImage = new Image();
    if (image.srcset) preloadImage.srcset = image.srcset;
    if (image.sizes) preloadImage.sizes = image.sizes;
    preloadImage.src = image.currentSrc || image.src;
  }

  #schedulePreloadAllPanelImages() {
    const preloadAll = () => {
      for (const panel of this.#panels) {
        this.#preloadPanelImage(panel);
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadAll, { timeout: 2000 });
      return;
    }

    window.setTimeout(preloadAll, 300);
  }

  #onKeyDown(event, tab) {
    const { key } = event;
    const currentIndex = this.#tabs.indexOf(tab);
    if (currentIndex < 0) return;

    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;

    event.preventDefault();

    let nextIndex = currentIndex;
    if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
    if (key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.#tabs.length;
    if (key === 'Home') nextIndex = 0;
    if (key === 'End') nextIndex = this.#tabs.length - 1;

    const nextTab = this.#tabs[nextIndex];
    if (!nextTab) return;

    this.#select(nextTab);
    nextTab.focus({ preventScroll: true });
  }
}

if (!customElements.get('product-ingredients-tabs')) {
  customElements.define('product-ingredients-tabs', ProductIngredientsTabs);
}
